import { ErrorAlert, FormInput, Text, WarningAlert } from '@/components';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import { WalletTokenInterface } from '@/interfaces/WalletTokenInterface';
import {
  formatNumberToAmountInTokenCurrencyString,
  formatNumberToAmountInUsdString,
} from '@/utils/formatUtils';
import { logError, logInfo } from '@/utils/logger';
import { WALLET } from '@/utils/strings';
import React, {
  FC,
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import WalletTokenListItem from '../WalletTokenListItem/WalletTokenListItem';
interface WithdrawAssetFormProps {
  selectedAsset: WalletTokenInterface | null;
  onContinue: (data: { externalWalletAddress: string; amount: number }) => void;
  ref?: Ref<{
    submit: () => void;
    resetForm: () => void;
    setValues: (data: {
      externalWalletAddress: string;
      amount: number;
    }) => void;
  }>;
}
const WithdrawAssetForm: FC<WithdrawAssetFormProps> = forwardRef(
  ({ selectedAsset, onContinue }, ref: Ref<any>) => {
    const { wallets } = useWalletsContext();
    const {
      register,
      handleSubmit,
      formState: { errors },
      clearErrors,
      setValue,
      reset,
    } = useForm();
    const [externalWalletAddress, setExternalWalletAddress] = useState('');
    const [inputtingValue, setInputtingValue] = useState('');
    const [remainingBalance, setRemainingBalance] = useState<number | null>(
      null,
    );
    const [remainingBalanceInUsd, setRemainingBalanceInUsd] = useState<
      number | null
    >(null);
    const [conversionRate, setConversionRate] = useState<number | null>(null);
    useImperativeHandle(ref, () => ({
      submit: handleSubmit(onSubmit),
      resetForm: () => {
        reset();
        setExternalWalletAddress('');
        setInputtingValue('');
        setRemainingBalance(null);
        setRemainingBalanceInUsd(null);
      },
      setValues: (data: { externalWalletAddress: string; amount: number }) => {
        setValue('externalWalletAddress', data.externalWalletAddress);
        setValue('amount', data.amount.toString());
        setExternalWalletAddress(data.externalWalletAddress);
        setInputtingValue(data.amount.toString());
        if (selectedAsset && conversionRate !== null) {
          const remaining = selectedAsset.balance ?? 0 - data.amount;
          setRemainingBalance(remaining);
          setRemainingBalanceInUsd(remaining * conversionRate);
        }
      },
    }));
    useEffect(() => {
      if (!selectedAsset) {
        setExternalWalletAddress('');
        setInputtingValue('');
        setRemainingBalance(null);
        setRemainingBalanceInUsd(null);
        setConversionRate(null);
      } else {
        const balance = selectedAsset.balance ?? 0;
        const aggregatedBalanceInUsd = parseFloat(
          selectedAsset.balanceInUsd?.toString() ?? '0',
        );
        setRemainingBalance(balance);
        setRemainingBalanceInUsd(aggregatedBalanceInUsd);
        setConversionRate(aggregatedBalanceInUsd / balance);
      }
    }, [selectedAsset]);
    const handleMaxClick = () => {
      if (selectedAsset && conversionRate !== null) {
        setValue('amount', selectedAsset.balance ?? '0');
        setInputtingValue(selectedAsset.balance.toString() ?? '0');
        setRemainingBalance(0);
        setRemainingBalanceInUsd(0);
        logInfo('Max button clicked', 'handleMaxClick', {
          amount: selectedAsset.balance,
        });
      }
    };
    const handleAmountInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value);
      setInputtingValue(e.target.value);
      if (selectedAsset && conversionRate !== null) {
        const remaining =
          selectedAsset.balance ?? 0 - (isNaN(value) ? 0 : value);
        setRemainingBalance(remaining);
        setRemainingBalanceInUsd(remaining * conversionRate);
      }
    };
    const onSubmit = (data: any) => {
      logInfo('Form submitted', 'onSubmit', data);
      if (selectedAsset && data.amount > 0 && data.externalWalletAddress) {
        onContinue(data);
      } else {
        logError('Validation failed', 'onSubmit', data);
      }
    };
    if (!selectedAsset) {
      return (
        <ErrorAlert
          title="No asset selected"
          description="Please select an asset to withdraw."
        />
      );
    }
    const selectedAssetWallet = wallets.find(
      (w) =>
        w.address === selectedAsset.walletAddress &&
        w.blockchain === selectedAsset.blockchain,
    );
    return (
      <div className="flex flex-col gap-md">
        <div className="flex flex-row gap-md items-start">
          <WalletTokenListItem
            token={selectedAsset}
            showBalance={true}
            showBalancePrefix={true}
            showWalletAddress={true}
            showWalletAddressPrefix={true}
            showWalletAddressCopyButton={true}
            showNetwork={true}
            showNetworkPrefix={true}
          />
        </div>
        <FormInput
          name="externalWalletAddress"
          label="Receiving wallet address"
          type="text"
          register={register}
          errors={errors}
          rules={{ required: 'Enter an external wallet address to continue' }}
          onInput={(e: any) => setExternalWalletAddress(e.target.value)}
          clearErrors={clearErrors}
        />
        <FormInput
          name="amount"
          label="Enter an amount"
          defaultValue={inputtingValue}
          type="number"
          register={register}
          errors={errors}
          rules={{
            required: 'Enter an amount to continue',
            validate: {
              maxBalance: (value) =>
                value <= selectedAsset.balance ||
                `${selectedAsset.balance} is the maximum possible value.`,
              minBalance: (value) =>
                value > 0 || 'Amount has to be greater than zero.',
            },
          }}
          onInput={handleAmountInput}
          suffix={
            <Text
              value="Max"
              color="accent"
              className="font-medium"
              onClick={handleMaxClick}
            />
          }
          clearErrors={clearErrors}
        />
        <Text
          value={`Remaining Balance ${formatNumberToAmountInTokenCurrencyString(
            remainingBalance ?? 0,
            selectedAsset.symbol || '',
          )} (${formatNumberToAmountInUsdString(remainingBalanceInUsd ?? 0)})`}
          size="sm"
          color="secondary"
        />
        <WarningAlert
          title={WALLET.WITHDRAWAL_ADDRESS_WARNING_TITLE}
          description={WALLET.WITHDRAWAL_ADDRESS_WARNING_DESCRIPTION.replace(
            '[asset_name]',
            `${selectedAsset.name} (${selectedAsset.symbol})`,
          ).replace(
            '[blockchain_name]',
            `${selectedAsset?.blockchainDisplayName}`,
          )}
        />
      </div>
    );
  },
);
export default WithdrawAssetForm;
